/**
 * OOS & Dispatch Block JavaScript
 * 
 * Handles:
 * - Out of stock notifications
 * - Dispatch estimates and dates
 * - Market-based messaging
 * - Notify me functionality with Klaviyo
 * - Alternative products display
 * - Dynamic updates on variant changes
 */

export function initOOSDispatchBlock() {
  
  
    const containers = document.querySelectorAll('.lpv2-main-product-oos-dispatch');
    if (containers.length === 0) return;

    containers.forEach(container => {
      const block = new OOSDispatchBlock(container);
      // Listen for state changes
      document.addEventListener('lpv2-state-changed', (event) => {
        const state = event.detail;
        block.updateMessage(state.selectedVariantId);
      });
    });
  
}

class OOSDispatchBlock {
  constructor(container) {
    this.container = container;
    this.blockId = container.id ? container.id.replace('lpv2-main-product-oos-dispatch-', '') : '';
    this.messageEl = container.querySelector(`#lpv2-oos-dispatch-message-${this.blockId}`);
    this.form = container.querySelector(`#lpv2-oos-notify-form-${this.blockId}`);
    this.messageContainer = container.querySelector(`#lpv2-oos-message-${this.blockId}`);
    
    this.init();
  }

  init() {
    this.setupVariantData();
    this.setupMarketMessages();
    this.bindEvents();
    this.updateMessage();
  }

  setupVariantData() {
    // Get variant data from global state instead of data attributes
    this.variantData = {};
    
    if (window.LPV2ProductVariants) {
      window.LPV2ProductVariants.forEach(variant => {
        this.variantData[variant.id] = {
          available: variant.available,
          dispatchDate: variant.dispatch_date || null,
          productDispatchDate: variant.product_dispatch_date || null
        };
      });
    }
  }

  setupMarketMessages() {
    this.marketMessages = {
      "us": "In stock, ready to ship from the US.",
      "eu": "In stock, ready to ship from the EU.",
      "ca": "In stock, ready to ship from Canada.",
      "me": "In stock, ready to ship from the UAE.",
      "default": "In stock, ready to ship from the UK."
    };
  }

  bindEvents() {
    // Handle notify me form submission
    if (this.form) {
      this.form.addEventListener('submit', (e) => this.handleNotifySubmit(e));
    }

    // Listen for variant changes from the old system
    document.addEventListener('variantChanged', (event) => {
      const variantId = event.detail.variantId;
      this.updateMessage(variantId);
    });
  }

  updateMessage(variantId = null) {
    if (!variantId) {
      // Get current variant ID from global state
      if (window.LPV2MainProductInitialState && window.LPV2MainProductInitialState.selectedVariantId) {
        variantId = window.LPV2MainProductInitialState.selectedVariantId;
      } else {
        variantId = this.container.dataset.variantId;
      }
    }

    const variant = this.variantData[variantId];
    if (!variant) return;

    const marketHandle = this.container.dataset.market;
    const marketMessage = this.marketMessages[marketHandle] || this.marketMessages.default;
    
    let messageHTML = '';

    if (variant.available) {
      const dispatchDate = variant.dispatchDate || variant.productDispatchDate;
      
      if (dispatchDate) {
        // Pre-order available
        messageHTML = `
          <svg class="lpv2-oos-dispatch-icon pre-order" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
          </svg>
          <span>
            Pre-order available. Estimated dispatch: 
            <span class="lpv2-oos-dispatch-date">${dispatchDate}</span>
          </span>
        `;
      } else {
        // In stock, ready to ship
        messageHTML = `
          <svg class="lpv2-oos-dispatch-icon in-stock" viewBox="0 0 24 24" fill="currentColor">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
          </svg>
          <span>${marketMessage}</span>
        `;
      }
    } else {
      // Out of stock
      const dispatchDate = variant.dispatchDate || variant.productDispatchDate;
      
      if (dispatchDate) {
        messageHTML = `
          <svg class="lpv2-oos-dispatch-icon out-of-stock" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11H7v-2h10v2z"/>
          </svg>
          <span>
            Out of stock. Back in stock on 
            <span class="lpv2-oos-dispatch-date">${dispatchDate}</span>
          </span>
        `;
      } else {
        messageHTML = `
          <svg class="lpv2-oos-dispatch-icon out-of-stock" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11H7v-2h10v2z"/>
          </svg>
          <span>Out of stock – Click 'Notify Me' for restock alert</span>
        `;
      }
    }

    if (this.messageEl) {
      this.messageEl.innerHTML = messageHTML;
    }
    
    // Show/hide notify section based on availability
    this.toggleNotifySection(variant.available);
  }

  toggleNotifySection(isAvailable) {
    const notifySection = this.container.querySelector('.lpv2-oos-notify-section');
    if (notifySection) {
      notifySection.style.display = isAvailable ? 'none' : 'block';
    }
  }

  handleNotifySubmit(event) {
    event.preventDefault();
    
    const emailInput = this.form.querySelector('input[type="email"]');
    const submitButton = this.form.querySelector('button[type="submit"]');
    const email = emailInput.value.trim();
    
    // Get current variant ID from global state
    let variantId = submitButton.dataset.variantId;
    if (!variantId && window.LPV2MainProductInitialState) {
      variantId = window.LPV2MainProductInitialState.selectedVariantId;
    }
    
    const productId = submitButton.dataset.productId;

    if (!email || !variantId) return;

    // Show loading state
    const originalText = submitButton.textContent;
    submitButton.textContent = 'Subscribing...';
    submitButton.disabled = true;
    this.messageContainer.style.display = 'none';

    // Klaviyo Back in Stock API
    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: email,
        product_id: productId,
        variant_id: variantId,
        platform: 'shopify'
      })
    };

    fetch('https://a.klaviyo.com/onsite/components/back-in-stock/subscribe', requestOptions)
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          this.showMessage('Success! You will be notified when the product is back in stock.', 'success');
          emailInput.value = '';
        } else {
          this.showMessage('Error: ' + (data.error_message || 'Unable to subscribe'), 'error');
        }
      })
      .catch(error => {
        this.showMessage('Error: Unable to process your request. Please try again later.', 'error');
      })
      .finally(() => {
        submitButton.textContent = originalText;
        submitButton.disabled = false;
      });
  }

  showMessage(message, type) {
    if (this.messageContainer) {
      this.messageContainer.textContent = message;
      this.messageContainer.className = `lpv2-oos-message ${type}`;
      this.messageContainer.style.display = 'block';
    }
  }

  destroy() {
    // Cleanup event listeners if needed
    if (this.form) {
      this.form.removeEventListener('submit', this.handleNotifySubmit);
    }
  }
}

// Auto-initialize if this script is loaded directly
if (typeof window !== 'undefined') {
  window.initOOSDispatchBlock = initOOSDispatchBlock;
} 